/* ASCEND lesson content — What an agent can touch (fundamentals 01).
   Seeds the vocabulary of real commands so later lessons can build on it. */
window.ASCEND_LESSONS = window.ASCEND_LESSONS || {};
window.ASCEND_LESSONS.capabilities = {
  id: "capabilities",
  tag: "fundamentals · capabilities",
  title: "What an agent can touch",
  estMinutes: 8,
  summary:
    "An agent works through ordinary tools. Judge each action by what it can read, change, execute, or send elsewhere.",

  blocks: [
    {
      type: "prose",
      html: `<p>When an agent works, it isn't doing anything exotic. It reaches for the same
        handful of tools you would use yourself. This lesson names each one, so you can always
        look at what an agent is doing and say plainly what's happening.</p>`,
    },
    {
      type: "heading",
      text: "Start with reading vs. changing",
    },
    {
      type: "prose",
      html: `<p>Every tool an agent uses falls into one of two buckets. It either
        <strong>looks at state</strong>, or it <strong>changes state</strong>. Reading has low
        mutation risk, but it is not automatically private or harmless: a read can expose secrets,
        cross a data boundary, or bring untrusted instructions into context. Changing deserves
        closer review because it can alter or destroy state.</p>`,
    },
    {
      type: "quiz",
      q: "Which action usually has the lowest mutation risk, while still requiring attention to sensitive data and scope?",
      options: [
        { text: "Reading — looking at files or searching them.", correct: true,
          why: "Reading does not mutate the file, but its path and contents can still be sensitive." },
        { text: "Changing — editing or deleting files.", correct: false,
          why: "Changes can be destructive. They're exactly the actions that deserve a look." },
        { text: "Running installs.", correct: false,
          why: "Installing runs code and changes your machine. Useful, but not a no-thought approval." },
      ],
    },
    {
      type: "heading",
      text: "The read tools",
    },
    {
      type: "prose",
      html: `<p>These are how an agent builds a picture of your project. They do not normally
        change files, but review what they can access. Configuration, environment files, private
        keys, customer data, and files outside the intended workspace may be unsafe to expose.</p>`,
    },
    {
      type: "tools",
      title: "read-only",
      items: [
        { cmd: "read / cat", what: "Open a file and show what's inside." },
        { cmd: "ls", what: "List what's in a folder." },
        { cmd: "grep", what: "Search for a word or pattern across many files at once." },
        { cmd: "glob", what: "Find files by pattern — for example, every file ending in <code>.js</code>." },
      ],
    },
    {
      type: "heading",
      text: "Change, execute, and communicate",
    },
    {
      type: "prose",
      html: `<p>Mutation is only one dimension. Commands may execute code, use elevated privilege,
        reach a network, or change an external system. Ask about the exact target, destination,
        current directory, credentials, reversibility, and blast radius.</p>`,
    },
    {
      type: "tools",
      title: "makes changes",
      items: [
        { cmd: "write", what: "Create a new file, or replace one entirely." },
        { cmd: "edit", what: "Change specific lines inside an existing file." },
        { cmd: "bash / run", what: "Run a command in your terminal. Because a terminal can do almost <em>anything</em>, this is the tool to watch most closely." },
      ],
    },
    {
      type: "quiz",
      q: "An agent uses its <code>bash / run</code> tool. Why does that one deserve the most attention?",
      options: [
        { text: "Because it can run essentially any command — from harmless to destructive.", correct: true,
          why: "A terminal is a universal tool. read/write touch files; the run tool can do everything else, including the dangerous things. Same reason the Security lesson lives mostly here." },
        { text: "Because it is slow.", correct: false,
          why: "Speed isn't the concern. Reach is — a terminal command can do almost anything." },
        { text: "Because it is read-only.", correct: false,
          why: "The opposite. Running commands can change your machine in any way you could yourself." },
      ],
    },
    {
      type: "heading",
      text: "Claude Code vs. Codex",
    },
    {
      type: "prose",
      html: `<p>You'll meet these same tools under different names depending on which assistant
        you use. The two most common are <strong>Claude Code</strong> (from Anthropic) and
        <strong>Codex</strong> (from OpenAI). Mostly the names and defaults differ, not the ideas:</p>
        <ul>
          <li>Both <strong>read and change</strong> files, and both can <strong>run</strong> terminal commands.</li>
          <li>Both support <strong>permissions and sandboxing</strong>, but what runs automatically depends on mode, configuration, workspace boundaries, and organization policy.</li>
          <li>Both can read plain-text project instructions you write for them:
            <code>CLAUDE.md</code> for Claude Code, <code>AGENTS.md</code> for Codex.</li>
        </ul>
        <p class="aside">Under the hood they differ more than this. Claude Code tends to give the
        model a dedicated tool for each job — a read tool, a search tool, and so on — while Codex
        tends to work through the shell, running ordinary commands like <code>cat</code> and
        <code>grep</code> to reach the same result. It rarely changes anything for you day to day,
        so the details wait for a later lesson.</p>
        <p>Learn the buckets — read, change, run — and you can move between assistants without
        relearning anything.</p>`,
    },
    {
      type: "quiz",
      q: "You want to give your coding agent persistent project guidance. Where can it go?",
      options: [
        { text: "CLAUDE.md for Claude Code, AGENTS.md for Codex.", correct: true,
          why: "Same broad idea, two names. Exact discovery, scope, precedence, and limits depend on the tool." },
        { text: "There's no way to give an agent reusable instructions.", correct: false,
          why: "There is, but instruction files guide behavior rather than guaranteeing it." },
        { text: "In a .env file, next to your secret keys.", correct: false,
          why: ".env holds secrets and config, not agent instructions." },
      ],
    },
    {
      type: "heading",
      text: "So what's safe to approve?",
    },
    {
      type: "prose",
      html: `<p>The full answer is its own lesson. The one-line version follows straight from the
        buckets: <strong>non-sensitive reads are usually lower risk; changes, execution, privilege,
        and outside communication require progressively more context.</strong> A permission prompt is
        one safeguard, not proof that everything consequential will pause.</p>
        <p>When you're ready to train that instinct on real, high-stakes commands, take
        <a class="link" href="/pages/lesson?id=security">Security: when to approve, when to deny &rarr;</a></p>`,
    },
    {
      type: "heading",
      text: "Exercise",
    },
    {
      type: "exercise",
      premium: false,
      title: "Name the capability and the risk",
      html: `<p>For each action, name its main capability: <strong>read</strong>, <strong>change</strong>,
        <strong>execute</strong>, or <strong>communicate</strong>. Then name one fact you would check.</p>
        <ol>
          <li><code>cat package.json</code></li>
          <li><code>rm -rf dist</code></li>
          <li><code>grep "api_key" .</code></li>
          <li><code>edit src/app.js</code></li>
          <li><code>ls -la</code></li>
          <li><code>npm install</code></li>
        </ol>
        <details>
          <summary>reveal the answers</summary>
          <div class="ex-answers">
            <p><strong>1. Read.</strong> Prints a file; changes nothing.</p>
            <p><strong>2. Change.</strong> Deletes the <code>dist</code> folder — a change, and a destructive one.</p>
            <p><strong>3. Read.</strong> Searches text; first check whether matching secrets may enter output or model context.</p>
            <p><strong>4. Change.</strong> Edits lines inside a file.</p>
            <p><strong>5. Read.</strong> Lists a folder.</p>
            <p><strong>6. Execute + change + network.</strong> It can download dependencies, run lifecycle scripts, and alter installed state or a lockfile.</p>
          </div>
        </details>`,
    },
    {
      type: "prose",
      html: `<p>From here on, narrate the capability and boundary:
        <em>what can this read, change, execute, or send—and where?</em> That narration is the skill.</p>
        <p>Next, learn how permissions, sandboxes, secrets, and untrusted instructions change the
        risk. <a class="link" href="/pages/lesson?id=security">continue to security &rarr;</a></p>`,
    },
  ],
};
